import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Drawer, Input, Button, Spin } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import Markdown from 'react-markdown';
import { aiChatApi, ChatMessage } from '~/services/ai-chat';

interface Props {
  open: boolean;
  onClose: () => void;
  booking: any;
}

function buildBookingContext(booking: any): string {
  if (!booking) return '';

  const client = booking.client
    ? booking.client.companyName || `${booking.client.firstName} ${booking.client.lastName}`
    : booking.tempClientData?.name || 'Unknown';

  const services = (booking.serviceTicket || [])
    .map((st: any) => {
      const date = st.startDatetime || st.serviceDatetime;
      const dateStr = date
        ? new Date(date).toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })
        : 'No date';
      const staff = st.staffs?.length || 0;
      const inv = st.inventories?.length || 0;
      return `- **${st.serial}** ${st.serviceType?.replace(/_/g, ' ')} · ${dateStr} · ${staff} staff · ${inv} items${st.scope ? ` · ${st.scope}` : ''}`;
    })
    .join('\n');

  return [
    `I'm looking at booking **${booking.referenceNumber}** for **${client}**.`,
    booking.sidemark ? `Sidemark: ${booking.sidemark}` : '',
    booking.project?.name ? `Project: ${booking.project.name}` : '',
    `Status: ${booking.status}`,
    `${booking.serviceTicket?.length || 0} service tickets:`,
    services,
    '',
    'Summarize this booking and let me know if anything needs attention.',
  ]
    .filter(Boolean)
    .join('\n');
}

// Store state outside component so it persists across open/close
const sessionCache = new Map<string, {
  messages: ChatMessage[];
  sessionId?: string;
}>();

export const BookingOzDrawer: FC<Props> = ({ open, onClose, booking }) => {
  const bookingId = booking?.id;
  const cached = bookingId ? sessionCache.get(bookingId) : undefined;

  const [messages, setMessages] = useState<ChatMessage[]>(cached?.messages || []);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(cached?.sessionId);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  // Restore from cache when booking changes
  useEffect(() => {
    if (bookingId) {
      const c = sessionCache.get(bookingId);
      if (c) {
        setMessages(c.messages);
        setSessionId(c.sessionId);
      } else {
        setMessages([]);
        setSessionId(undefined);
      }
    }
  }, [bookingId]);

  // Save to cache whenever messages change
  useEffect(() => {
    if (bookingId && messages.length > 0) {
      sessionCache.set(bookingId, { messages, sessionId });
    }
  }, [bookingId, messages, sessionId]);

  const sendMessage = useCallback(
    async (text: string, currentMessages: ChatMessage[]) => {
      const userMessage: ChatMessage = { role: 'user', content: text };
      const updatedMessages = [...currentMessages, userMessage];
      setMessages(updatedMessages);
      setIsStreaming(true);

      setMessages([...updatedMessages, { role: 'assistant', content: '' }]);

      try {
        let accumulated = '';
        for await (const result of aiChatApi.streamChat(updatedMessages, sessionId)) {
          if (result.sessionId) setSessionId(result.sessionId);
          if (result.text) {
            accumulated += result.text;
            setMessages([
              ...updatedMessages,
              { role: 'assistant', content: accumulated },
            ]);
          }
        }
      } catch {
        setMessages([
          ...updatedMessages,
          { role: 'assistant', content: 'Sorry, something went wrong.' },
        ]);
      } finally {
        setIsStreaming(false);
      }
    },
    [sessionId],
  );

  const handleSummarize = useCallback(() => {
    const context = buildBookingContext(booking);
    sendMessage(context, messages);
  }, [booking, messages, sendMessage]);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    setInput('');
    sendMessage(trimmed, messages);
  }, [input, isStreaming, messages, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <Drawer
      title={
        <div className="flex items-center gap-2">
          <span className="font-bold">Oz</span>
          <span className="text-xs text-gray-400">
            {booking?.referenceNumber}
          </span>
        </div>
      }
      placement="right"
      width={420}
      open={open}
      onClose={onClose}
      mask={false}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        wrapper: { boxShadow: '-4px 0 12px rgba(0,0,0,0.08)' },
      }}
    >
      <div className="flex flex-col h-full">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {isEmpty && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="text-2xl font-bold text-gray-300 mb-3">Oz</div>
              <p className="text-xs text-gray-400 mb-4 max-w-[240px]">
                Ask anything about booking {booking?.referenceNumber}, or get a quick summary.
              </p>
              <button
                onClick={handleSummarize}
                disabled={isStreaming}
                className="text-xs border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors"
              >
                Summarize this booking
              </button>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-3 py-2 text-xs select-text ${
                  msg.role === 'user'
                    ? 'bg-black text-white whitespace-pre-wrap'
                    : 'bg-gray-100 text-black border border-gray-200 [&_p]:mb-1.5 [&_ul]:list-disc [&_ul]:pl-3 [&_ol]:list-decimal [&_ol]:pl-3 [&_li]:mb-0.5 [&_strong]:font-bold [&_h3]:font-bold [&_h3]:mb-1'
                }`}
              >
                {msg.role === 'assistant' && (
                  <div className="text-[9px] font-bold text-gray-400 mb-1 uppercase tracking-wider">
                    Oz
                  </div>
                )}
                {msg.content ? (
                  msg.role === 'assistant' ? (
                    <Markdown>{msg.content}</Markdown>
                  ) : (
                    msg.content
                  )
                ) : isStreaming && i === messages.length - 1 ? (
                  <Spin size="small" />
                ) : null}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-gray-200">
          <div className="flex gap-2 items-end">
            <Input.TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about this booking..."
              autoSize={{ minRows: 1, maxRows: 3 }}
              disabled={isStreaming}
              className="flex-1 !border-gray-300 !rounded-none text-xs"
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={isStreaming}
              disabled={!input.trim()}
              className="!bg-black !border-black !rounded-none h-[32px]"
              size="small"
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
};
